/**
 * Utility class to manage navigation between panels.
 * Panels can optionally be declared using the `withPanel` method, and then
 * referenced by name. This is not mandatory though.
 */
export default class PanelSet {
  panels = new Map();
  panelOptions = new WeakMap();
  history  = [];
  currentIndex = -1;

  /**
   * Chainable method.
   * Adds/declares a panel in the object's list for future usage.
   * The optional third argument allows to specify custom show()
   * and hide() functions. If those functions are defined and return a falsy
   * value, the default behaviour will be cancelled, if they return a truthy
   * value, the default behaviour will be applied.
   */
  withPanel(name, panel, {show, hide} = {}) {
    this.panels.set(name, panel);
    this.panelOptions.set(panel, {show, hide});
    return this;
  }

  getPanel(name) {
    return this.panels.get(name);
  }

  getCurrentPanel() {
    if (!this.history.length) return null;
    return this.history[this.currentIndex];
  }

  /**
   * Chainable method.
   * Forgets the whole history, except for the current panel.
   */
  resetHistory() {
    const currentPanel = this.getCurrentPanel();
    if (currentPanel) {
      this.history = [currentPanel];
      this.currentIndex = 0;
    }
    else {
      this.history = [];
      this.currentIndex = -1;
    }
    return this;
  }

  /**
   * Chainable method.
   * Show the specified panel and hide others.
   *
   * @param nameOrPanel The name of a previously declared panel,
   *                    or any DOM element acting as a panel (which doesn't have
   *                    to be declared first in this case).
   */
  goTo(nameOrPanel) {
    const panel = this.panels.get(nameOrPanel) ?? nameOrPanel;
    if (!panel) throw new Error('Unknown panel', { nameOrPanel });

    const currentPanel = this.getCurrentPanel();
    if (panel === currentPanel) return this;

    this.history = this.history.slice(0, this.currentIndex+1);
    this.history.push(panel);
    this.currentIndex = this.history.length-1;

    if (currentPanel) this.#hide(currentPanel);
    this.#show(panel);
    return this;
  }

  /**
   * Chainable method.
   * Go to the panel that was last active before the current one.
   */
  goBack() {
    if (this.history.length < 2) return this;
    this.#hide(this.getCurrentPanel());
    this.currentIndex--;
    this.#show(this.getCurrentPanel());
    return this;
  }

  /**
   * Chainable method.
   * Move forward in history (cancels a previous goBack).
   */
  goForward() {
    if (this.currentIndex >= this.history.length-1) return this;
    this.#hide(this.getCurrentPanel());
    this.currentIndex++;
    this.#show(this.getCurrentPanel());
    return this;
  }

  /**
   * Show the specified panel without hiding others.
   */
  #show(panel) {
    const panelOptions = this.panelOptions.get(panel);
    if (panelOptions?.show && !panelOptions?.show(panel)) return;
    panel.setAttribute('aria-hidden', false);
  }
  /**
   * Hide the specified panel without touching others.
   */
  #hide(panel) {
    const panelOptions = this.panelOptions.get(panel);
    if (panelOptions?.hide && !panelOptions?.hide(panel)) return;
    panel.setAttribute('aria-hidden', true);
  }
}
